import { isDataGridCellNestedInteraction } from './isDataGridCellNestedInteraction';

const recentCellClicks = new Set();

/**
 * Prevents the same cell click from running twice when both AG Grid and DOM handlers fire.
 *
 * @param {string} cellKey
 * @returns {boolean}
 */
export function shouldSkipDuplicateCellClick(cellKey) {
    if (!cellKey || recentCellClicks.has(cellKey)) return true;

    recentCellClicks.add(cellKey);
    requestAnimationFrame(() => recentCellClicks.delete(cellKey));

    return false;
}

/**
 * @param {import('ag-grid-community').CellClickedEvent | object} params
 * @returns {string}
 */
export function getCellClickKey(params) {
    const rowId = params?.node?.id ?? params?.data?.id ?? '';
    const colId =
        params?.column?.getColId?.() ?? params?.colDef?.colId ?? params?.colDef?.field ?? '';

    return `${rowId}:${colId}`;
}

/**
 * Resolves the cell-level onClick handler from renderer params or colDef.
 *
 * @param {object | undefined} params
 * @returns {((params: object) => void) | undefined}
 */
export function getCellOnClick(params) {
    return params?.onClick ?? params?.colDef?.cellRendererParams?.onClick;
}

/**
 * Resolves the children-row onClick handler from renderer params or colDef.
 *
 * @param {object | undefined} params
 * @returns {((params: object) => void) | undefined}
 */
export function getChildrenOnClick(params) {
    return (
        params?.childrenParams?.onClick ??
        params?.colDef?.cellRendererParams?.childrenParams?.onClick
    );
}

/**
 * Reads a cell renderer param from AG Grid params or colDef.cellRendererParams.
 *
 * @param {object | undefined} params
 * @param {string} key
 * @returns {*}
 */
export function getCellRendererParam(params, key) {
    return params?.[key] ?? params?.colDef?.cellRendererParams?.[key];
}

/**
 * Invokes a column cell onClick from renderer/grid params.
 *
 * @param {import('ag-grid-community').CellClickedEvent | object} params
 * @returns {boolean} true when a handler ran
 */
export function invokeCellClickHandler(params) {
    if (!params) return false;

    const rendererParams = params.colDef?.cellRendererParams ?? {};
    const onClick = params.data?.isChildren
        ? getChildrenOnClick({
              colDef: params.colDef,
              childrenParams: rendererParams.childrenParams ?? params.childrenParams,
          })
        : getCellOnClick(params);

    if (!onClick) return false;

    const cellKey = getCellClickKey(params);
    if (shouldSkipDuplicateCellClick(cellKey)) return false;

    if (getCellRendererParam(params, 'forceUrl') && getCellRendererParam(params, 'getUrl')) {
        params.event?.preventDefault?.();
    }

    onClick(params);
    return true;
}

/** @deprecated Use invokeCellClickHandler */
export function invokeCellClickFromGridEvent(event) {
    return invokeCellClickHandler(event);
}

/**
 * Wires cellRendererParams.onClick to AG Grid's native colDef.onCellClicked and
 * suppresses default grid mouse handling for interactive cells.
 *
 * @param {import('ag-grid-community').ColDef} colDef
 * @returns {import('ag-grid-community').ColDef}
 */
export function attachColDefCellClickHandler(colDef) {
    if (!colDef) return colDef;

    const rendererParams = colDef.cellRendererParams ?? {};
    const hasClickHandler =
        Boolean(rendererParams.onClick) || Boolean(rendererParams.childrenParams?.onClick);

    if (!hasClickHandler || colDef.onCellClicked) return colDef;

    colDef.cellRendererParams = {
        ...rendererParams,
        suppressMouseEventHandling: rendererParams.suppressMouseEventHandling ?? (() => true),
    };

    colDef.onCellClicked = (event) => {
        if (isDataGridCellNestedInteraction(event?.event?.target)) return;
        invokeCellClickHandler(event);
    };

    return colDef;
}
